<template>
  <div class="col-12 col-xl-12">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <!-- Card header -->
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
            {{ $t("assignments.my_assignments") }}
          </h6>
        </div>
      </div>

      <!-- Card body -->
      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="isLoading && assignments.length === 0"
            :message="$t('assignments.loading')"
            class="py-5"
          />

          <template v-else>
            <!-- Mobile view -->
            <div class="d-block d-xl-none">
              <div v-if="!isLoading && assignments.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-tasks fa-2x mb-2"></i>
                <p>{{ $t("assignments.no_assignments") }}</p>
              </div>

              <div
                v-for="assignment in assignments"
                :key="'mobile-' + assignment.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0 text-sm">{{ assignment.title }}</h6>
                    <span class="badge badge-sm" :class="getStatusBadge(assignment)">
                      {{ getStatusText(assignment) }}
                    </span>
                  </div>

                  <div class="mt-2">
                    <p class="text-xs text-muted mb-1">
                      <i class="fas fa-calendar-day me-1"></i>
                      {{ $t("assignments.due_date") }}: {{ formatDate(assignment.due_date) }}
                    </p>
                    <p class="text-xs mb-1">
                      {{ assignment.description || $t("assignments.no_description") }}
                    </p>
                  </div>

                  <div class="d-flex justify-content-end mt-2">
                    <router-link
                      v-if="!assignment.submitted"
                      :to="{ name: 'student.assignments.submit', params: { assignmentId: assignment.id } }"
                      class="btn text-primary btn-sm"
                    >
                      <i class="fas fa-paper-plane me-1"></i> {{ $t("assignments.submit") }}
                    </router-link>
                    <a
                      v-if="assignment.submitted"
                      :href="assignment.submission.file_url"
                      target="_blank"
                      class="btn text-primary btn-sm me-2"
                    >
                      <i class="fas fa-file-download me-1"></i> {{ $t("assignments.view_submission") }}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop table -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ $t("assignments.title") }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ $t("assignments.description") }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ $t("assignments.due_date") }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder text-center opacity-7">
                    {{ $t("assignments.status") }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">
                    {{ $t("assignments.actions") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!isLoading && assignments.length === 0">
                  <td colspan="5" class="text-center py-4 text-muted">
                    <i class="fas fa-tasks fa-2x mb-2"></i>
                    <p>{{ $t("assignments.no_assignments") }}</p>
                  </td>
                </tr>

                <template v-if="!isLoading">
                  <tr v-for="assignment in assignments" :key="assignment.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ assignment.title }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ assignment.description || $t("assignments.no_description") }}
                      </p>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ formatDate(assignment.due_date) }}
                      </p>
                    </td>
                    <td class="align-middle text-center">
                      <span class="badge badge-sm" :class="getStatusBadge(assignment)">
                        {{ getStatusText(assignment) }}
                      </span>
                    </td>
                    <td class="align-middle text-center pe-4">
                      <div class="d-inline-flex">
                        <router-link
                          v-if="!assignment.submitted"
                          :to="{ name: 'student.assignments.submit', params: { assignmentId: assignment.id } }"
                          class="btn text-primary btn-sm"
                        >
                          <i class="fas fa-paper-plane me-1"></i> {{ $t("assignments.submit") }}
                        </router-link>
                        <a
                          v-if="assignment.submitted"
                          :href="assignment.submission.file_url"
                          target="_blank"
                          class="btn text-primary btn-sm me-2"
                        >
                          <i class="fas fa-file-download me-1"></i> {{ $t("assignments.view_submission") }}
                        </a>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>

            <div class="d-flex justify-content-center mt-4">
              <Pagination :pagination="pagination" @page-changed="onPageChanged" />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useToast } from "vue-toastification";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import Pagination from "@/components/common/PaginationControls.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const toast = useToast();

const assignments = ref([]);
const isLoading = ref(false);
const pagination = ref({
  currentPage: 1,
  perPage: 10,
  totalItems: 0,
  totalPages: 0,
  from: 0,
  to: 0,
});

const formatDate = (dateString) => {
  if (!dateString) return "";
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString("ar", options);
};

const getStatusBadge = (assignment) => {
  if (assignment.submitted) {
    return assignment.submission.grade ? "bg-success" : "bg-info";
  }
  const now = new Date();
  const dueDate = new Date(assignment.due_date);
  return now > dueDate ? "bg-danger" : "bg-warning";
};

const getStatusText = (assignment) => {
  if (assignment.submitted) {
    return assignment.submission.grade
      ? t("assignments.graded")
      : t("assignments.submitted");
  }
  const now = new Date();
  const dueDate = new Date(assignment.due_date);
  return now > dueDate ? t("assignments.late") : t("assignments.pending");
};

const fetchAssignments = async () => {
  isLoading.value = true;
  try {
    const response = await api.get("/student/student-assignments", {
      params: {
        page: pagination.value.currentPage,
        per_page: pagination.value.perPage,
      },
    });

    assignments.value = response.data.data.filter((a) => a !== null);

    if (response.data.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page,
        perPage: response.data.meta.per_page,
        totalItems: response.data.meta.total,
        totalPages: response.data.meta.last_page,
        from: response.data.meta.from,
        to: response.data.meta.to,
      };
    }
  } catch (error) {
    toast.error(error.response?.data?.message || t("assignments.load_failed"));
  } finally {
    isLoading.value = false;
  }
};

const onPageChanged = (page) => {
  pagination.value.currentPage = page;
  fetchAssignments();
};

onMounted(() => {
  fetchAssignments();
});
</script>
